import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, RotateCcw, Send } from "lucide-react";

/** Phase 1 — REWORK banner. When Legal returns a contract for rework, the
 *  submitter sees this amber banner with the reason. They can click "Resubmit"
 *  to send back with a required response comment (backend also unlocks intake
 *  fields for edit per user's choice). */
export default function ReworkBanner({ contract, onDone }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  if (!contract || contract.stage !== "rework_requested" || !contract.rework_open) return null;

  const email = (user?.email || "").toLowerCase();
  const isSubmitter = (
    (contract.submitted_by_email || "").toLowerCase() === email
    || (contract.submitter_email || "").toLowerCase() === email
    || (contract.on_behalf_of_email || "").toLowerCase() === email
    || user?.role === "it_admin"
  );

  const submit = async () => {
    if (!comment.trim()) return toast.error("A response comment is required");
    setBusy(true);
    try {
      await api.post(`/contracts/${contract.id}/resubmit`, {
        response_comment: comment.trim(),
        updates: {},  // full form re-edit happens in the intake tab — this endpoint just closes the loop
      });
      toast.success("Resubmitted — Legal has been notified");
      setOpen(false); setComment("");
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Resubmit failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-amber-50 border border-amber-300 rounded-md p-4 my-3" data-testid="rework-banner">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-bold text-amber-900 text-sm mb-1">Legal returned this contract for rework</div>
          <div className="text-xs text-amber-800 mb-2">
            <b>Requested by:</b> {contract.rework_requested_by_name || "Legal"} · {contract.rework_requested_at ? new Date(contract.rework_requested_at).toLocaleString() : ""}
          </div>
          <div className="text-sm text-amber-900 bg-white/60 border border-amber-200 rounded p-2 mb-3">
            {contract.rework_reason || "(no reason provided)"}
          </div>
          {isSubmitter ? (
            <>
              <div className="text-[11px] text-amber-800 mb-2">
                You can edit any intake field and re-upload documents from the Intake tab.
                When you're done, click <b>Resubmit</b> below with a short response so Legal knows what you changed.
              </div>
              {open ? (
                <div className="space-y-2">
                  <textarea value={comment} onChange={e => setComment(e.target.value)}
                            data-testid="resubmit-comment" rows={3} maxLength={4000}
                            placeholder="What did you fix? (required)"
                            className="w-full p-2 text-sm border border-amber-300 rounded resize-none bg-white" />
                  <div className="flex items-center gap-2">
                    <button onClick={() => { setOpen(false); setComment(""); }}
                            className="h-8 px-3 text-xs border border-slate-200 rounded bg-white">Cancel</button>
                    <button onClick={submit} disabled={busy || !comment.trim()}
                            data-testid="resubmit-submit"
                            className="ml-auto h-8 px-3 text-xs font-bold text-white rounded flex items-center gap-1 bg-amber-600 disabled:opacity-50">
                      <Send className="w-3 h-3" /> {busy ? "Resubmitting…" : "Resubmit to Legal"}
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setOpen(true)}
                        data-testid="resubmit-open"
                        className="h-8 px-3 text-xs font-bold text-white rounded flex items-center gap-1 bg-amber-600">
                  <RotateCcw className="w-3 h-3" /> Resubmit to Legal
                </button>
              )}
            </>
          ) : (
            <div className="text-[11px] text-amber-800">
              The submitter has been notified. Only they can resubmit this contract.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
